/**
 * SprintReportModal.jsx
 *
 * Full-featured Sprint Reports Dashboard modal.
 *
 * Layout:
 *   [Header]
 *   [Left: Filter bar + Archive table]  |  [Right Sidebar: Custom Generation]
 *   [Footer stats bar]
 *
 * When user generates / views a report â†’ a SEPARATE full-screen overlay
 * (z-[2000]) slides in on top of everything, with a â† Back button that
 * returns to this modal without closing it.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spin, Modal } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import axios from 'axios';
import {
    generateSprintReport,
    fetchSprints,
    fetchTasks,
    fetchProjects,
    fetchSprintReports,
    saveSprintReport,
    markReportAsRead,
    shareSprintReport,
} from '../api/projectManagementApi';
import { filterTasksToSprint } from './sprintUtils';

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// CONSTANTS
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const DEPARTMENTS = [
    'Marketing', 'Development', 'Human Resource', 'Finance',
    'Customer Support', 'Business Development', 'Project Management Office',
];

const REPORT_CATEGORIES = [
    'Complete Sprint Report',
    'Executive Summary',
    'Team Performance Report',
    'Project Performance Report',
    'Task Performance Report',
    'Productivity Report',
    'Time Tracking Report',
];

// Each section: key (used for includeSections toggle), label, and the payload
// fields it maps to. The viewer uses `key` to decide whether to render a block.
const INCLUDE_SECTIONS = [
    { key: 'executiveSummary', label: 'Executive Summary', icon: null },
    { key: 'projectPerformance', label: 'Project Performance', icon: null },
    { key: 'teamPerformance', label: 'Team Performance', icon: null },
    { key: 'taskPerformance', label: 'Task Performance', icon: null },
    { key: 'timeTracking', label: 'Time Tracking', icon: null },
    { key: 'productivityMetrics', label: 'Productivity Metrics', icon: null },
    { key: 'aiInsights', label: 'AI Insights', icon: 'auto_awesome' },
    { key: 'issuesRisks', label: 'Issues & Risks', icon: null },
    { key: 'recommendations', label: 'Recommendations', icon: null },
];

const STATUS_BADGE = {
    Completed: 'bg-emerald-100 text-emerald-700',
    Closed: 'bg-slate-100 text-slate-700',
    Close: 'bg-slate-100 text-slate-700',
    'In Progress': 'bg-blue-100 text-blue-700',
    Active: 'bg-blue-100 text-blue-700',
    'Under Review': 'bg-amber-100 text-amber-700',
    'On Hold': 'bg-orange-100 text-orange-700',
    Cancelled: 'bg-red-100 text-red-700',
    Backlog: 'bg-slate-100 text-slate-600',
};

const fmtDate = (d, opts) => d ? new Date(d).toLocaleDateString('en-GB', opts) : 'â€”';
const fmtShort = d => fmtDate(d, { day: 'numeric', month: 'short' });
const fmtLong = d => fmtDate(d, { day: 'numeric', month: 'long', year: 'numeric' });
const fmtTime = d => d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// FULL-SCREEN REPORT VIEWER
// Rendered as a separate overlay (z-[2000]) so the modal stays beneath it.
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function ReportViewer({ reportItem, onBack }) {
    const report = reportItem?.payload || null;
    const secKey = (k) => {
        const sections = reportItem?.includedSections || report?._includedSections;
        if (sections && sections[k] !== undefined) {
            return sections[k];
        }
        return true; // default: show
    };

    if (!report) return null;

    return (
        <div className="fixed inset-0 z-[2000] bg-[#f8f9fa] flex flex-col overflow-hidden">
            <style>{`
                @media print {
                    .rv-no-print { display:none!important; }
                    body { font-size:11pt; color:#000!important; }
                    section { page-break-inside:avoid; }
                    @page { size:A4 portrait; margin:15mm; }
                }
                .rv-scroll::-webkit-scrollbar { width:5px; }
                .rv-scroll::-webkit-scrollbar-track { background:transparent; }
                .rv-scroll::-webkit-scrollbar-thumb { background:#c2c6d2; border-radius:10px; }
            `}</style>

            {/* Viewer Header */}
            <header className="rv-no-print flex-shrink-0 flex items-center justify-between px-8 py-4 bg-white border-b border-slate-200 shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-bold text-[#003263] transition-colors"
                    >
                        <span className="material-symbols-outlined text-base">arrow_back</span>
                        Back to Reports
                    </button>
                    <div className="h-6 w-px bg-slate-200" />
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            {reportItem?.reportCategory || 'Sprint Report'}
                        </p>
                        <h1 className="font-headline font-black text-lg text-[#003263] leading-tight">
                            {reportItem?.sprintName || 'Report'}
                            {(() => {
                                const teamPart = reportItem?.payload?.meta?.filterLabel
                                    ?.split('|')
                                    .map(f => f.trim())
                                    .find(f => f.startsWith('team:'));
                                return teamPart
                                    ? <span className="text-sm font-semibold text-slate-500 ml-2">— {teamPart.replace('team:', '')}</span>
                                    : null;
                            })()}
                        </h1>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 font-medium">
                        Generated {fmtLong(reportItem?.createdAt)}
                    </span>
                    {reportItem?.reportType === 'manual' && (
                        <button
                            onClick={async () => {
                                if (!window.confirm('Regenerate AI summary? This will replace the existing AI-generated sections.')) return;
                                try {
                                    await axios.post(`/project-management/sprint-reports/${reportItem._id}/regenerate`);
                                    alert('AI summary regenerated successfully. Reload the report to see changes.');
                                    window.location.reload();
                                } catch (e) {
                                    alert('Failed to regenerate: ' + (e.response?.data?.message || e.message));
                                }
                            }}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-xs font-bold transition-all hover:opacity-90 bg-violet-600"
                        >
                            <span className="material-symbols-outlined text-sm">auto_awesome</span>
                            Regenerate AI
                        </button>
                    )}
                    <button
                        onClick={() => window.print()}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-xs font-bold transition-all hover:opacity-90"
                        style={{ background: 'linear-gradient(90deg,#03488a,#006591)' }}
                    >
                        <span className="material-symbols-outlined text-sm">print</span>
                        Print / PDF
                    </button>
                </div>
            </header>

            {/* Viewer Body */}
            <div className="flex-1 overflow-y-auto rv-scroll">
                <div className="max-w-5xl mx-auto px-6 py-10 space-y-10">

                    {/* â”€â”€ Report Title Card â”€â”€ */}
                    <div className="rounded-2xl p-8 text-white" style={{ background: 'linear-gradient(135deg,#03488a 0%,#006591 100%)' }}>
                        <p className="text-blue-200 text-xs font-bold uppercase tracking-widest mb-2">
                            {reportItem?.reportCategory || 'Complete Sprint Report'}
                        </p>
                        <h2 className="font-headline font-black text-3xl">{reportItem?.sprintName || 'Report'}</h2>
                        <p className="text-blue-200 text-sm mt-2">
                            {fmtShort(reportItem?.startDate)} – {fmtShort(reportItem?.endDate)}
                            {reportItem?.createdAt && ` Â· Generated ${fmtLong(reportItem.createdAt)} ${fmtTime(reportItem.createdAt)}`}
                        </p>
                        {/* Active filters badge row */}
                        {reportItem?.payload?.meta?.filterLabel && (
                            <div className="flex flex-wrap gap-2 mt-4">
                                {reportItem.payload.meta.filterLabel.split('|').map((f, i) => (
                                    <span key={i} className="bg-white/20 text-white text-[10px] font-bold px-3 py-1 rounded-full">{f}</span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* â”€â”€ 1. Sprint Overview â”€â”€ always shown */}
                    {report.totalTasks !== undefined && (
                        <section>
                            <SectionHeading>Sprint Overview</SectionHeading>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 p-5 rounded-2xl border border-slate-200">
                                <StatCell label="Total Tasks" value={report.totalTasks} large />
                                <StatCell label="Date Range" value={report.dateRange} />
                                <StatCell label="Effort (Est / Actual)" value={
                                    <>{report.effort?.totalEst} / <span className={report.effort?.isOverrun ? 'text-red-500' : 'text-emerald-500'}>{report.effort?.totalAct}</span></>
                                } />
                                <StatCell label="Completion Rate" value={report.completionRate + '%'}
                                    cls={report.completionRate >= 80 ? 'text-emerald-500' : report.completionRate >= 50 ? 'text-amber-500' : 'text-red-500'}
                                    large />
                            </div>
                        </section>
                    )}

                    {/* â”€â”€ 2. Task Summary badges â”€â”€ always shown */}
                    {report.counts && (
                        <section>
                            <SectionHeading>Task Summary</SectionHeading>
                            <div className="flex flex-wrap gap-3">
                                <Badge cls="bg-emerald-100 text-emerald-800 border-emerald-200">{report.counts.completed} Completed</Badge>
                                <Badge cls="bg-slate-200 text-slate-700 border-slate-300">{report.counts.carryForward} Carry Forward</Badge>
                                <Badge cls="bg-orange-100 text-orange-800 border-orange-200">{report.counts.onHold} On Hold</Badge>
                                <Badge cls="bg-red-100 text-red-800 border-red-200">{report.counts.cancelled} Cancelled</Badge>
                                <Badge cls="bg-amber-100 text-amber-800 border-amber-200">{report.counts.overdue} Overdue</Badge>
                                <Badge cls="bg-blue-100 text-blue-800 border-blue-200">{report.counts.underReview} Under Review</Badge>
                            </div>
                        </section>
                    )}

                    {/* â”€â”€ 3. Executive Summary â”€â”€ */}
                    {secKey('executiveSummary') && report.aiSummaryHTML && (
                        <section>
                            <SectionHeading icon="smart_toy">AI Executive Summary</SectionHeading>
                            <div
                                className="text-sm bg-blue-50 p-6 rounded-2xl border border-blue-100 text-blue-900 leading-relaxed space-y-3"
                                dangerouslySetInnerHTML={{ __html: report.aiSummaryHTML }}
                            />
                        </section>
                    )}

                    {/* â”€â”€ 4. Task Performance â”€â”€ */}
                    {secKey('taskPerformance') && report.taskRows?.length > 0 && (
                        <section>
                            <SectionHeading>Task Performance</SectionHeading>
                            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                                        <tr>
                                            {['Task Name', 'Project', 'Type', 'Assignee', 'Est.', 'Actual', 'Variance', 'Status', 'Final Stage'].map(h => (
                                                <th key={h} className="p-3">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {report.taskRows.map((row, i) => (
                                            <tr key={i} className={row.overrun ? 'bg-red-50/40' : 'hover:bg-slate-50'}>
                                                <td className="p-3 font-semibold max-w-[200px] truncate" title={row.title}>{row.title}</td>
                                                <td className="p-3 text-slate-500 text-[10px]">{row.project}</td>
                                                <td className="p-3">
                                                    <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${row.projectType === 'Client' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                                                        {row.projectType || 'In-house'}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-slate-500">{row.assignee}</td>
                                                <td className="p-3">{row.est}</td>
                                                <td className="p-3">{row.actual}</td>
                                                <td className={`p-3 font-bold ${row.variance?.startsWith?.('+') ? 'text-red-500' : row.variance?.startsWith?.('-') ? 'text-emerald-500' : 'text-slate-400'}`}>{row.variance}</td>
                                                <td className="p-3">
                                                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${STATUS_BADGE[row.status] || STATUS_BADGE.Backlog}`}>{row.status}</span>
                                                </td>
                                                <td className="p-3 text-[10px] font-semibold text-slate-600">{row.finalPipelineStage || row.status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* â”€â”€ 5. Team Performance â”€â”€ */}
                    {secKey('teamPerformance') && report.teamPerformance?.length > 0 && (
                        <section>
                            <SectionHeading>Team Performance</SectionHeading>
                            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                                        <tr>
                                            {['Member', 'Dept', 'Assigned', 'Completed', 'Pending', 'Carry Fwd', 'Est. Hrs', 'Actual Hrs', 'Avg Review', 'Efficiency'].map(h => (
                                                <th key={h} className="p-3">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {report.teamPerformance.map((m, i) => (
                                            <tr key={i} className="hover:bg-slate-50">
                                                <td className="p-3 font-semibold">{m.name}</td>
                                                <td className="p-3 text-[10px] text-slate-500">{m.department}</td>
                                                <td className="p-3 text-center">{m.assignedTasks}</td>
                                                <td className="p-3 text-center text-emerald-600 font-bold">{m.completedTasks}</td>
                                                <td className="p-3 text-center text-blue-600">{m.pendingTasks ?? 0}</td>
                                                <td className="p-3 text-center text-amber-600">{m.carryForwardTasks}</td>
                                                <td className="p-3 text-right text-slate-500">{m.estimatedHours}</td>
                                                <td className="p-3 text-right text-slate-500">{m.actualHours}</td>
                                                <td className="p-3 text-center text-slate-600 text-[10px]">{m.avgReviewTime ?? '—'}</td>
                                                <td className="p-3 text-center">
                                                    {m.efficiencyScore !== null
                                                        ? <span className={`font-bold ${m.efficiencyScore >= 80 ? 'text-emerald-500' : m.efficiencyScore >= 50 ? 'text-amber-500' : 'text-red-500'}`}>{m.efficiencyScore}%</span>
                                                        : <span className="text-slate-400">N/A</span>
                                                    }
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* â"€â"€ 5b. Star Performers â"€â"€ */}
                    {secKey('teamPerformance') && report.starPerformers && Object.keys(report.starPerformers).length > 0 && (
                        <section>
                            <SectionHeading icon="emoji_events">⭐ Star Performers</SectionHeading>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {Object.entries(report.starPerformers).map(([dept, star]) => (
                                    <div key={dept} className="bg-gradient-to-br from-yellow-50 to-amber-50 p-5 rounded-2xl border-2 border-amber-200 shadow-sm">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-2xl">🏆</span>
                                            <span className="text-[9px] font-black text-amber-700 uppercase tracking-wider">{dept}</span>
                                        </div>
                                        <h4 className="font-black text-lg text-slate-800">{star.name}</h4>
                                        <div className="mt-3 space-y-1 text-xs">
                                            <div className="flex justify-between"><span className="text-amber-700">Completed:</span><b className="text-emerald-600">{star.completedTasks}</b></div>
                                            <div className="flex justify-between"><span className="text-amber-700">Efficiency:</span><b className="text-blue-600">{star.efficiency}%</b></div>
                                            <div className="flex justify-between"><span className="text-amber-700">Productivity:</span><b className="text-violet-600">{star.productivity}%</b></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* â"€â"€ 5c. Department Performance â"€â"€ */}
                    {secKey('teamPerformance') && report.departmentPerformance?.length > 0 && (
                        <section>
                            <SectionHeading>Department Performance</SectionHeading>
                            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                                        <tr>
                                            {['Department', 'Planned', 'Completed', 'Carry Fwd', 'Rate', 'Est. Hrs', 'Effort Hrs', 'Efficiency'].map(h => (
                                                <th key={h} className="p-3">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {report.departmentPerformance.map((d, i) => (
                                            <tr key={i} className="hover:bg-slate-50">
                                                <td className="p-3 font-semibold">{d.department}</td>
                                                <td className="p-3 text-center">{d.planned}</td>
                                                <td className="p-3 text-center text-emerald-600 font-bold">{d.completed}</td>
                                                <td className="p-3 text-center text-amber-600">{d.carryForward}</td>
                                                <td className="p-3 text-center">
                                                    <span className={`font-bold ${d.rate >= 80 ? 'text-emerald-500' : d.rate >= 50 ? 'text-amber-500' : 'text-red-500'}`}>{d.rate}%</span>
                                                </td>
                                                <td className="p-3 text-right text-slate-500">{d.estHours}</td>
                                                <td className="p-3 text-right text-slate-500">{d.effortHours}</td>
                                                <td className="p-3 text-center">
                                                    <span className={`font-bold ${d.efficiency >= 80 ? 'text-emerald-500' : d.efficiency >= 50 ? 'text-amber-500' : 'text-red-500'}`}>{d.efficiency}%</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* â”€â”€ 6. Time Tracking / Effort Summary â”€â”€ */}
                    {secKey('timeTracking') && report.effort && (
                        <section>
                            <SectionHeading>Time Tracking Summary</SectionHeading>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="border border-slate-200 p-5 rounded-2xl">
                                    <p className="font-bold text-sm text-slate-700">Efficiency Score</p>
                                    <p className={`text-3xl font-black mt-2 ${report.effort.efficiency >= 80 ? 'text-emerald-500' : report.effort.efficiency >= 60 ? 'text-amber-500' : 'text-red-500'}`}>
                                        {report.effort.efficiency !== null ? `${report.effort.efficiency}%` : 'N/A'}
                                    </p>
                                    <p className={`text-xs mt-1 ${report.effort.isOverrun ? 'text-red-500' : 'text-emerald-600'}`}>
                                        {report.effort.isOverrun ? `↓ ${report.effort.overrun} over budget` : `↑ On target`}
                                    </p>
                                </div>
                                <div className="border border-slate-200 p-5 rounded-2xl">
                                    <p className="font-bold text-sm text-slate-700">Est. vs Actual</p>
                                    <p className="text-2xl font-black text-[#03488a] mt-2">
                                        {report.effort.totalEst} <span className="text-slate-300">/</span>{' '}
                                        <span className={report.effort.isOverrun ? 'text-red-500' : 'text-emerald-500'}>{report.effort.totalAct}</span>
                                    </p>
                                    <p className={`text-xs mt-1 ${report.effort.isOverrun ? 'text-red-500' : 'text-emerald-600'}`}>
                                        {report.effort.isOverrun ? `+${report.effort.overrun} overrun` : 'Under budget'}
                                    </p>
                                </div>
                                <div className="border border-slate-200 p-5 rounded-2xl">
                                    <p className="font-bold text-sm text-slate-700">Completion Rate</p>
                                    <p className={`text-3xl font-black mt-2 ${report.completionRate >= 80 ? 'text-emerald-500' : report.completionRate >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
                                        {report.completionRate}%
                                    </p>
                                    <p className="text-xs mt-1 text-slate-400">
                                        {report.counts?.completed} of {report.totalTasks} tasks done
                                    </p>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* 7. Project Performance */}
                    {secKey('projectPerformance') && report.projectBreakdown?.length > 0 && (
                        <section>
                            <SectionHeading>Project Performance</SectionHeading>
                            <div className="grid md:grid-cols-2 gap-4">
                                {report.projectBreakdown.map((p, i) => (
                                    <div key={i} className="bg-slate-50 p-5 rounded-2xl border border-slate-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-bold text-sm truncate" title={p.name}>{p.name}</h4>
                                                <span className={`text-[8px] font-black px-2 py-0.5 rounded-full ${p.type === 'Client' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}`}>
                                                    {p.type}
                                                </span>
                                            </div>
                                            <span className={`text-sm font-black ${p.rate >= 80 ? 'text-emerald-500' : p.rate >= 50 ? 'text-amber-500' : 'text-red-500'}`}>{p.rate}%</span>
                                        </div>
                                        {p.clientName && <p className="text-[10px] text-slate-500 mb-3">Client: {p.clientName}</p>}
                                        <div className="h-1.5 bg-slate-200 rounded-full mb-3">
                                            <div className={`h-full rounded-full ${p.rate >= 80 ? 'bg-emerald-500' : p.rate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${p.rate}%` }} />
                                        </div>
                                        <div className="space-y-1 text-sm">
                                            <div className="flex justify-between"><span className="text-slate-400">Total tasks:</span><b>{p.total}</b></div>
                                            <div className="flex justify-between"><span className="text-slate-400">Completed:</span><b className="text-emerald-500">{p.completed}</b></div>
                                            {p.delayed > 0 && <div className="flex justify-between"><span className="text-slate-400">Overdue:</span><b className="text-red-500">{p.delayed}</b></div>}
                                            {p.onHold > 0 && <div className="flex justify-between"><span className="text-slate-400">On Hold:</span><b className="text-orange-500">{p.onHold}</b></div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* 8. Productivity Metrics */}
                    {secKey('productivityMetrics') && report.comparison && (
                        <section>
                            <SectionHeading>Productivity Metrics</SectionHeading>
                            <div className="grid md:grid-cols-2 gap-4 bg-slate-50 p-5 rounded-2xl border border-slate-200 text-sm">
                                {Object.entries(report.comparison).map(([k, v]) => (
                                    <div key={k} className="flex justify-between border-b border-slate-200 pb-2 last:border-0">
                                        <span className="text-slate-500 capitalize">{k.replace(/([A-Z])/g, ' $1')}</span>
                                        <b>{typeof v === 'number' ? `${v}%` : v}</b>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* 9. AI Insights */}
                    {secKey('aiInsights') && report.aiInsights && (
                        <section>
                            <SectionHeading icon="auto_awesome">AI Insights</SectionHeading>
                            <div className="text-sm bg-violet-50 p-6 rounded-2xl border border-violet-100 text-violet-900 leading-relaxed space-y-2"
                                dangerouslySetInnerHTML={{ __html: typeof report.aiInsights === 'string' ? report.aiInsights : JSON.stringify(report.aiInsights) }}
                            />
                        </section>
                    )}

                    {/* 10. Issues & Risks */}
                    {secKey('issuesRisks') && report.risks?.length > 0 && (
                        <section>
                            <SectionHeading icon="warning">Issues & Risks</SectionHeading>
                            <div className="space-y-3">
                                {report.risks.map((risk, i) => (
                                    <div key={i} className={`flex gap-3 p-5 rounded-2xl border ${['High', 'Critical'].includes(risk.severity) ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                                        <span className={`material-symbols-outlined text-xl flex-shrink-0 mt-0.5 ${['High', 'Critical'].includes(risk.severity) ? 'text-red-500' : 'text-amber-500'}`}>warning</span>
                                        <div>
                                            <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1">{risk.severity} Risk</p>
                                            <p className="text-sm font-semibold text-slate-700">{risk.description}</p>
                                            {risk.action && <p className="text-xs text-slate-500 mt-1">→ {risk.action}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* 10b. Carry Forward Analysis */}
                    {secKey('taskPerformance') && report.carryForwardAnalysis?.length > 0 && (
                        <section>
                            <SectionHeading icon="redo">Carry Forward Analysis</SectionHeading>
                            <div className="space-y-3">
                                {report.carryForwardAnalysis.map((task, i) => (
                                    <div key={i} className="flex gap-4 p-5 rounded-2xl bg-amber-50 border border-amber-200">
                                        <span className="material-symbols-outlined text-amber-500 flex-shrink-0 mt-0.5">arrow_forward</span>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h4 className="font-bold text-sm text-slate-800">{task.title}</h4>
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${STATUS_BADGE[task.status] || STATUS_BADGE.Backlog}`}>{task.status}</span>
                                            </div>
                                            <p className="text-xs text-slate-500">Project: {task.project} · Assignee: {task.assignee}</p>
                                            <p className="text-xs text-amber-700 mt-2 italic">Reason: {task.reason}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* 11. Recommendations */}
                    {secKey('recommendations') && report.recommendations && (
                        <section>
                            <SectionHeading icon="lightbulb">Recommendations</SectionHeading>
                            <div className="text-sm bg-emerald-50 p-6 rounded-2xl border border-emerald-100 text-emerald-900 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: typeof report.recommendations === 'string' ? report.recommendations : JSON.stringify(report.recommendations) }}
                            />
                        </section>
                    )}

                </div>
            </div>
        </div>
    );
}
function SectionHeading({ children, icon }) {
    return (
        <h3 className="text-lg font-black text-[#003263] font-headline mb-4 border-b border-slate-200 pb-2 flex items-center gap-2">
            {icon && <span className="material-symbols-outlined text-[#003263]">{icon}</span>}
            {children}
        </h3>
    );
}

function StatCell({ label, value, large, cls = '' }) {
    return (
        <div>
            <p className="text-slate-400 text-xs uppercase font-bold tracking-wider">{label}</p>
            <p className={`font-semibold mt-1 ${large ? `text-2xl font-black ${cls}` : cls}`}>{value}</p>
        </div>
    );
}

function Badge({ children, cls }) {
    return <span className={`px-4 py-2 rounded-lg text-xs font-bold border ${cls}`}>{children}</span>;
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// MAIN COMPONENT
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export default function SprintReportModal({ open, onClose, tasks = [], selectedSprint = null }) {
    const currentAdmin = useSelector(selectCurrentAdmin);

    // â”€â”€ Archive list filter state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const [sprintFilter, setSprintFilter] = useState('all');
    const [dateRangeFilter, setDateRangeFilter] = useState('');
    const [deptFilter, setDeptFilter] = useState('all');
    const [teamFilter, setTeamFilter] = useState('all');
    const [projectTypeFilter, setProjectTypeFilter] = useState('all');
    const [projectFilter, setProjectFilter] = useState('all');
    const [reportTypeFilter, setReportTypeFilter] = useState('all');
    const [unreadOnly, setUnreadOnly] = useState(false);

    // â”€â”€ Repository data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const [sprints, setSprints] = useState([]);
    const [reports, setReports] = useState([]);
    const [projects, setProjects] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loadingData, setLoadingData] = useState(false);

    // â”€â”€ Custom generation state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const [customScope, setCustomScope] = useState('current');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [customDept, setCustomDept] = useState('all');
    const [customTeam, setCustomTeam] = useState('all');
    const [customProjectType, setCustomProjectType] = useState('CLIENT');
    const [customProject, setCustomProject] = useState('all');
    const [customReportCategory, setCustomReportCategory] = useState('Complete Sprint Report');
    const [includeSections, setIncludeSections] = useState(
        Object.fromEntries(INCLUDE_SECTIONS.map(s => [s.key, true]))
    );
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState(null);

    // â”€â”€ Full-screen viewer state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const [viewerReport, setViewerReport] = useState(null); // if set â†’ full-screen viewer shown

    // â”€â”€ Load data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const loadRepositoryData = useCallback(async () => {
        setLoadingData(true);
        try {
            const [sprintsRes, reportsRes, projectsRes, empRes] = await Promise.all([
                fetchSprints(),
                fetchSprintReports(),
                fetchProjects(),
                axios.get('/employeesNames').catch(() => ({ data: {} })),
            ]);

            const sprintList = sprintsRes.data?.sprints || [];
            setSprints(sprintList);

            if (reportsRes.data?.success) setReports(reportsRes.data.sprints || reportsRes.data.reports || []);
            if (projectsRes.data?.success) setProjects(projectsRes.data.result || projectsRes.data.projects || []);

            const emp = empRes?.data?.names || empRes?.data?.employees || empRes?.data?.result || [];
            setEmployees(Array.isArray(emp) ? emp : []);
        } catch (e) {
            console.error('[SprintReportModal] loadRepositoryData:', e);
        } finally {
            setLoadingData(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            loadRepositoryData();
        }
    }, [open, loadRepositoryData]);

    // Reset on close
    useEffect(() => {
        if (!open) {
            setViewerReport(null);
            setGenError(null);
            setSprintFilter('all');
        }
    }, [open]);

    // â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const handleMarkAsRead = async (id) => {
        try {
            await markReportAsRead(id);
            setReports(prev => prev.map(r =>
                r._id === id ? { ...r, isReadBy: [...(r.isReadBy || []), currentAdmin?._id] } : r
            ));
        } catch { /* silent */ }
    };

    const handleDeleteReport = async (id, name) => {
        Modal.confirm({
            title: 'Delete Report?',
            content: `Permanently delete "${name}" report?`,
            okText: 'Delete', okType: 'danger',
            onOk: async () => {
                try {
                    await axios.delete(`/project-management/sprint-reports/${id}`);
                    setReports(prev => prev.filter(r => r._id !== id));
                } catch { /* silent */ }
            },
        });
    };

    const handleShareReport = async (id, channel) => {
        try {
            await shareSprintReport(id, channel);
            loadRepositoryData();
        } catch { /* silent */ }
    };

    const openViewer = (reportItem, opts = {}) => {
        const { markAsRead = true } = opts;
        setViewerReport(reportItem);
        if (markAsRead && currentAdmin && !reportItem.isReadBy?.includes(currentAdmin._id)) {
            handleMarkAsRead(reportItem._id);
        }
    };

    // â”€â”€ Apply includeSections mask before saving â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const applyMask = (payload) => {
        const masked = { ...payload };
        const MAP = {
            executiveSummary: ['aiSummaryHTML'],
            projectPerformance: ['projectBreakdown'],
            teamPerformance: ['teamPerformance'],
            taskPerformance: ['taskRows', 'carryForwardAnalysis'],
            timeTracking: ['effort'],
            productivityMetrics: ['comparison'],
            aiInsights: ['aiInsights'],
            issuesRisks: ['risks', 'issuesBlockers'],
            recommendations: ['recommendations'],
        };
        Object.entries(MAP).forEach(([k, fields]) => {
            if (!includeSections[k]) fields.forEach(f => delete masked[f]);
        });
        return masked;
    };

    // â”€â”€ Core: generate + save + open viewer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const doGenerateAndSave = async (scopeTasks, sprintName, sprintObj, filterLabel) => {
        if (!scopeTasks?.length) {
            setGenError('No tasks found for the selected filters. Please broaden your selection and try again.');
            return;
        }

        const { data } = await generateSprintReport(scopeTasks);
        if (!data.success) throw new Error(data.message || 'AI generation failed');

        const maskedPayload = {
            ...applyMask(data.report),
            meta: {
                project: customProject,
                dept: customDept,
                team: customTeam,
                filterLabel,
            },
        };

        // Track which sections were included (for viewer-side conditional rendering)
        const includedSections = { ...includeSections };

        const saveRes = await saveSprintReport({
            sprintId: sprintObj?._id || null,
            sprintName,
            startDate: sprintObj?.startDate || customStartDate || new Date(),
            endDate: sprintObj?.endDate || customEndDate || new Date(),
            reportType: 'manual',
            reportCategory: customReportCategory,
            payload: maskedPayload,
            includedSections,
        });

        const saved = saveRes.data?.success
            ? { ...saveRes.data.report, includedSections }
            : { sprintName, payload: maskedPayload, reportCategory: customReportCategory, reportType: 'manual', createdAt: new Date(), includedSections };

        loadRepositoryData();
        return saved;
    };

    // â”€â”€ Duplicate check helper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const checkDuplicate = (sprintId, sprintName) => {
        return reports.find(r =>
            (sprintId ? r.sprintId === sprintId : r.sprintName === sprintName) &&
            r.reportCategory === customReportCategory
        );
    };

    // â”€â”€ Main Generate handler â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const handleGenerateCustom = async () => {
        setGenError(null);
        setGenerating(true);
        try {
            // â”€â”€ 1. Build server-side query params â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
            const qp = { limit: 'all' };
            const selProject = customProject !== 'all' ? projects.find(p => p.name === customProject) : null;
            if (selProject) qp.projectId = selProject._id;
            // Fix: customTeam holds e.id (Admin _id) — that's what tasks store as assignee
            if (customTeam !== 'all') qp.assignee = customTeam;

            // â”€â”€ 2. Fetch tasks â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
            const res = await fetchTasks(qp);
            const allFetched = res.data?.result || res.data?.tasks || res.data?.data || [];

            // â”€â”€ 3. Determine scope + filter by date â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
            let scopeTasks = [];
            let sprintName = '';
            let sprintObj = null;
            const filterParts = [];

            if (selProject) filterParts.push(`project:${customProject}`);
            // Use e.id (Admin _id) to match name label correctly
            if (customTeam !== 'all') filterParts.push(`team:${employees.find(e => e.id === customTeam)?.name || customTeam}`);
            if (customDept !== 'all') filterParts.push(`dept:${customDept}`);

            if (customScope === 'current') {
                sprintObj = selectedSprint;
                sprintName = selectedSprint?.name || 'Current Sprint';
                scopeTasks = sprintObj
                    ? filterTasksToSprint(allFetched, sprintObj.startDate, sprintObj.endDate)
                    : allFetched;
            } else if (customScope === 'previous') {
                const prev = [...sprints]
                    .filter(s => s.status === 'completed')
                    .sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];
                if (!prev) {
                    setGenError('No completed previous sprint found in the database.');
                    return;
                }
                sprintObj = prev;
                sprintName = prev.name;
                scopeTasks = filterTasksToSprint(allFetched, prev.startDate, prev.endDate);
            } else if (customScope === 'custom') {
                if (!customStartDate || !customEndDate) {
                    setGenError('Please select both start and end dates for the custom date range.');
                    return;
                }
                sprintName = `Custom (${fmtShort(customStartDate)} – ${fmtShort(customEndDate)})`;
                scopeTasks = filterTasksToSprint(allFetched, customStartDate, customEndDate);
                filterParts.push(`range:${customStartDate} – ${customEndDate}`);
            }

            // â”€â”€ 4. Apply dept filter (client-side) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
            // Dept filter: build a Set of Admin _ids belonging to the selected dept
            // (t.assignee?.department is never populated — department lives on Employee schema)
            if (customDept !== 'all') {
                const deptAdminIds = new Set(
                    employees.filter(e => e.department === customDept).map(e => String(e.id))
                );
                scopeTasks = scopeTasks.filter(t => {
                    const assigneeId = String(t.assignee?._id || t.assignee || '');
                    return deptAdminIds.has(assigneeId);
                });
            }

            const filterLabel = filterParts.join(' | ');

            if (!scopeTasks.length) {
                setGenError(`No tasks found${filterParts.length ? ` for (${filterLabel})` : ''}. Try broadening your filters.`);
                return;
            }

            // â”€â”€ 5. Duplicate detection â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
            const dupe = checkDuplicate(sprintObj?._id, sprintName);
            if (dupe) {
                setGenerating(false);
                Modal.confirm({
                    title: 'Report Already Exists',
                    content: `A "${customReportCategory}" report for "${sprintName}" already exists. What would you like to do?`,
                    okText: 'Generate New',
                    cancelText: 'View Existing',
                    onOk: async () => {
                        setGenerating(true);
                        try {
                            const saved = await doGenerateAndSave(scopeTasks, sprintName, sprintObj, filterLabel);
                            if (saved) openViewer(saved, { markAsRead: false });
                        } catch (e) {
                            setGenError(e.message || 'Generation failed.');
                        } finally {
                            setGenerating(false);
                        }
                    },
                    onCancel: () => {
                        // Add includedSections to existing so viewer can use them
                        openViewer({ ...dupe, includedSections: { ...includeSections } });
                    },
                });
                return;
            }

            // â”€â”€ 6. Generate â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
            const saved = await doGenerateAndSave(scopeTasks, sprintName, sprintObj, filterLabel);
            if (saved) openViewer(saved, { markAsRead: false });

        } catch (e) {
            console.error('[SprintReportModal] handleGenerateCustom:', e);
            setGenError(e?.response?.data?.message || e.message || 'Server error. Please try again.');
        } finally {
            setGenerating(false);
        }
    };

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    if (!open) return null;

    // â”€â”€ Derived data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const activeSprintObj = sprints.find(s => s.status === 'active') || selectedSprint;
    const prevSprintObj = [...sprints].filter(s => s.status === 'completed').sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];
    const unreadCount = reports.filter(r => currentAdmin && !r.isReadBy?.includes(currentAdmin._id)).length;

    const filteredReports = reports.filter(r => {
        if (sprintFilter !== 'all' && r.sprintId !== sprintFilter) return false;
        if (reportTypeFilter !== 'all' && r.reportType !== reportTypeFilter) return false;
        if (unreadOnly && currentAdmin && r.isReadBy?.includes(currentAdmin._id)) return false;
        if (deptFilter !== 'all') { const d = r.payload?.meta?.dept; if (d && d !== deptFilter) return false; }
        if (teamFilter !== 'all') { const t = r.payload?.meta?.team; if (t && t !== teamFilter) return false; }
        if (projectFilter !== 'all') {
            const p = r.payload?.meta?.project;
            if (p) { if (p !== projectFilter) return false; }
            else if (!r.payload?.projectBreakdown?.some(pb => pb.name === projectFilter)) return false;
        }
        return true;
    });

    // â”€â”€ Filter projects by type for sidebar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const filteredSidebarProjects = projects.filter(p => {
        if (customProjectType === 'CLIENT') return p.isClientProject === true;
        if (customProjectType === 'IN-HOUSE') return p.isClientProject === false || p.isClientProject == null;
        return true;
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // RENDER
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    return (
        <>
            <style>{`
                @keyframes pulse-blue { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:.6;transform:scale(1.05);} }
                .srm-pulse { animation:pulse-blue 2s cubic-bezier(.4,0,.6,1) infinite; }
                .srm-scroll::-webkit-scrollbar{width:5px;height:5px;}
                .srm-scroll::-webkit-scrollbar-track{background:transparent;}
                .srm-scroll::-webkit-scrollbar-thumb{background:#c2c6d2;border-radius:10px;}
                .srm-scroll::-webkit-scrollbar-thumb:hover{background:#737782;}
            `}</style>

            {/* â”€â”€ FULL-SCREEN REPORT VIEWER (z-2000, above modal) â”€â”€ */}
            {viewerReport && (
                <ReportViewer
                    reportItem={viewerReport}
                    onBack={() => setViewerReport(null)}
                />
            )}

            {/* â”€â”€ MODAL BACKDROP + CONTAINER â”€â”€ */}
            <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#2e3132]/30 backdrop-blur-md p-3 md:p-5">
                <div className="bg-white w-full max-w-[96vw] h-[95vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-[#c2c6d2]/20">

                    {/* â”€â”€ HEADER â”€â”€ */}
                    <header className="flex items-center justify-between px-8 py-5 flex-shrink-0" style={{ background: 'linear-gradient(90deg,#03488a 0%,#006591 100%)' }}>
                        <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-white text-2xl">analytics</span>
                            <h1 className="text-white font-headline font-extrabold text-xl tracking-tight uppercase">Sprint Reports</h1>
                        </div>
                        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors text-white">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </header>

                    {/* â”€â”€ CONTENT ROW â”€â”€ */}
                    <div className="flex flex-1 overflow-hidden">

                        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
                            LEFT: Filter bar + Archive Table
                        â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
                        <div className="flex-1 flex flex-col overflow-hidden bg-[#f3f4f5]/30 min-w-0">

                            {/* Filter Bar */}
                            <section className="px-6 py-5 bg-white shadow-sm z-10 flex-shrink-0">
                                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-4">

                                    {/* Sprint */}
                                    <FilterSelect label="Sprint" value={sprintFilter} onChange={setSprintFilter}>
                                        <option value="all">All Sprints</option>
                                        {sprints.map(s => <option key={s._id} value={s._id}>{s.name}{s.status === 'active' ? ' (Active)' : ''}</option>)}
                                    </FilterSelect>

                                    {/* Date Range (text search) */}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-[#424750] uppercase tracking-wider px-1">Date Range</label>
                                        <div className="flex items-center bg-[#f3f4f5] rounded-lg h-9 px-3 gap-2">
                                            <span className="material-symbols-outlined text-sm text-[#737782]">calendar_today</span>
                                            <input type="text" value={dateRangeFilter} onChange={e => setDateRangeFilter(e.target.value)}
                                                className="bg-transparent border-none p-0 text-xs w-full outline-none placeholder:text-[#737782]"
                                                placeholder="e.g. Jul 2026" />
                                        </div>
                                    </div>

                                    {/* Dept */}
                                    <FilterSelect label="Dept." value={deptFilter} onChange={setDeptFilter}>
                                        <option value="all">All Depts</option>
                                        {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                                    </FilterSelect>

                                    {/* Team */}
                                    <FilterSelect label="Team" value={teamFilter} onChange={setTeamFilter}>
                                        <option value="all">All Teams</option>
                                        {employees.map(e => <option key={e._id} value={e._id}>{e.name}{e.surname ? ` ${e.surname}` : ''}</option>)}
                                    </FilterSelect>

                                    {/* Project Type */}
                                    <FilterSelect label="Project Type" value={projectTypeFilter} onChange={v => { setProjectTypeFilter(v); setProjectFilter('all'); }}>
                                        <option value="all">Select Type</option>
                                        <option value="CLIENT">Client</option>
                                        <option value="IN-HOUSE">In-house</option>
                                    </FilterSelect>

                                    {/* Project */}
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-[#424750] uppercase tracking-wider px-1">Project</label>
                                        <select
                                            value={projectFilter}
                                            onChange={e => setProjectFilter(e.target.value)}
                                            disabled={projectTypeFilter === 'all'}
                                            className={`w-full bg-[#f3f4f5] border-none rounded-lg text-xs h-9 px-2 outline-none ${projectTypeFilter === 'all' ? 'opacity-50 cursor-not-allowed italic text-[#737782]' : 'text-[#191c1d]'}`}
                                        >
                                            <option value="all">{projectTypeFilter === 'all' ? 'Select Type first' : 'All Projects'}</option>
                                            {projects.map(p => <option key={p._id} value={p.name}>{p.name}</option>)}
                                        </select>
                                    </div>

                                    {/* Report Type */}
                                    <FilterSelect label="Report Type" value={reportTypeFilter} onChange={setReportTypeFilter}>
                                        <option value="all">Both</option>
                                        <option value="system">System Gen.</option>
                                        <option value="manual">Manual</option>
                                    </FilterSelect>
                                </div>

                                {/* Unread + Clear */}
                                <div className="flex items-center justify-between">
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input type="checkbox" checked={unreadOnly} onChange={e => setUnreadOnly(e.target.checked)}
                                            className="rounded text-[#003263] focus:ring-[#003263] w-4 h-4" />
                                        <span className="text-[11px] font-semibold text-[#191c1d] uppercase tracking-wide">Unread Only</span>
                                    </label>
                                    <button
                                        onClick={() => {
                                            setSprintFilter('all'); setDateRangeFilter(''); setDeptFilter('all');
                                            setTeamFilter('all'); setProjectTypeFilter('all'); setProjectFilter('all');
                                            setReportTypeFilter('all'); setUnreadOnly(false);
                                        }}
                                        className="text-[#003263] text-[11px] font-extrabold hover:underline flex items-center gap-1 uppercase tracking-wider"
                                    >
                                        <span className="material-symbols-outlined text-sm">filter_list_off</span>
                                        Clear All Filters
                                    </button>
                                </div>
                            </section>

                            {/* Archive Table */}
                            <div className="flex-1 overflow-y-auto srm-scroll p-6">
                                <div className="bg-white rounded-xl border border-[#c2c6d2]/30 overflow-hidden shadow-sm">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr style={{ background: 'linear-gradient(90deg,#03488a 0%,#006591 100%)' }}>
                                                {['Sprint Name', 'Sprint Duration', 'Report Type', 'Generated On', 'Delivery', 'Actions'].map((h, i) => (
                                                    <th key={h} className={`px-4 py-3.5 text-[10px] font-black text-white uppercase tracking-widest ${i === 0 ? 'rounded-tl-xl' : ''} ${i === 5 ? 'rounded-tr-xl text-center' : ''}`}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-[#f3f4f5]">
                                            {loadingData ? (
                                                <tr><td colSpan={6} className="py-16 text-center">
                                                    <div className="flex flex-col items-center gap-3 text-[#424750]">
                                                        <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#03488a' }} />} />
                                                        <span className="text-xs">Loading archived reportsâ€¦</span>
                                                    </div>
                                                </td></tr>
                                            ) : filteredReports.length === 0 ? (
                                                <tr><td colSpan={6} className="py-16 text-center text-[#424750]">
                                                    <span className="material-symbols-outlined text-4xl opacity-30 block mb-2">analytics</span>
                                                    <p className="text-sm font-semibold">No reports found</p>
                                                    <p className="text-xs mt-1 opacity-70">Use the panel on the right to generate your first report.</p>
                                                </td></tr>
                                            ) : filteredReports.map(r => {
                                                const isUnread = currentAdmin && !r.isReadBy?.includes(currentAdmin._id);
                                                const genFmt = r.createdAt ? `${fmtDate(r.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })}, ${fmtTime(r.createdAt)}` : 'â€”';
                                                return (
                                                    <tr key={r._id} className={`hover:bg-[#003263]/5 transition-colors group ${isUnread ? 'bg-[#c9e6ff]/10' : ''}`}>
                                                        <td className="px-4 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <span className="font-bold text-[#191c1d]">{r.sprintName}</span>
                                                                {isUnread && <span className="bg-[#28b5fd] text-[#004c6e] text-[10px] font-black px-2 py-0.5 rounded srm-pulse">NEW</span>}
                                                            </div>
                                                            {r.reportCategory && <p className="text-[10px] text-[#737782] mt-0.5">{r.reportCategory}</p>}
                                                            {r.payload?.meta?.filterLabel && (
                                                                <div className="flex flex-wrap gap-1 mt-1">
                                                                    {r.payload.meta.filterLabel.split('|').map((f, i) => (
                                                                        <span key={i} className="bg-[#003263]/10 text-[#003263] text-[9px] font-bold px-2 py-0.5 rounded-full">
                                                                            {f.trim()}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 text-xs text-[#424750] font-medium whitespace-nowrap">
                                                            {fmtShort(r.startDate)} – {fmtShort(r.endDate)}
                                                        </td>
                                                        <td className="px-4 py-4 text-center">
                                                            {r.reportType === 'system'
                                                                ? <span className="bg-[#d5e3ff] text-[#004788] px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter">SYSTEM GEN.</span>
                                                                : <span className="bg-[#ffdbca] text-[#773300] px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter">MANUAL</span>
                                                            }
                                                        </td>
                                                        <td className="px-4 py-4 text-xs text-[#424750] font-medium whitespace-nowrap">{genFmt}</td>
                                                        <td className="px-4 py-4">
                                                            <div className="flex items-center gap-2">
                                                                <span className="material-symbols-outlined text-base" style={{ color: r.deliveryStatus?.email === 'sent' ? '#2E7D32' : '#c2c6d2' }} title="Email">mail</span>
                                                                <span className="material-symbols-outlined text-base" style={{ color: r.deliveryStatus?.slack === 'sent' ? '#2E7D32' : '#c2c6d2' }} title="Slack">chat</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4">
                                                            <div className="flex items-center justify-center gap-1">
                                                                <ActionBtn icon="visibility" title="View" cls="text-[#003263] hover:bg-[#003263]/10" onClick={() => openViewer({ ...r, includedSections: r.includedSections || Object.fromEntries(INCLUDE_SECTIONS.map(s => [s.key, true])) })} />
                                                                <ActionBtn icon="download" title="Download PDF" cls="text-[#424750] hover:bg-[#e1e3e4]" onClick={() => window.print()} />
                                                                <ActionBtn icon="share" title="Share" cls="text-[#424750] hover:bg-[#e1e3e4]"
                                                                    onClick={() => Modal.confirm({
                                                                        title: 'Share Report',
                                                                        content: 'Choose a channel:',
                                                                        okText: 'ðŸ“§ Email',
                                                                        cancelText: 'ðŸ’¬ Slack',
                                                                        onOk: () => handleShareReport(r._id, 'email'),
                                                                        onCancel: () => handleShareReport(r._id, 'slack'),
                                                                    })}
                                                                />
                                                                <ActionBtn icon="delete" title="Delete" cls="text-red-400 hover:bg-red-50 opacity-0 group-hover:opacity-100" onClick={() => handleDeleteReport(r._id, r.sprintName)} />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
                            RIGHT SIDEBAR: Custom Generation
                        â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
                        <aside className="w-[420px] flex-shrink-0 bg-[#f3f4f5] border-l border-[#c2c6d2]/30 flex flex-col overflow-hidden">
                            <div className="flex items-center gap-2 px-6 py-5 border-b border-[#c2c6d2]/30">
                                <span className="material-symbols-outlined text-[#003263]">auto_awesome</span>
                                <h2 className="font-headline font-bold text-[#191c1d] uppercase tracking-tight text-sm">Custom Generation</h2>
                            </div>

                            <div className="flex-1 overflow-y-auto srm-scroll px-6 py-5 space-y-6">

                                {/* â”€â”€ Report Scope â”€â”€ */}
                                <div className="space-y-3">
                                    <SideLabel>Report Scope</SideLabel>
                                    <div className="flex flex-col gap-2">
                                        <ScopeCard
                                            label="Current Sprint"
                                            sub={activeSprintObj ? `${activeSprintObj.name} (Active)` : 'No active sprint'}
                                            value="current"
                                            selected={customScope}
                                            onChange={setCustomScope}
                                        />
                                        <ScopeCard
                                            label="Previous Sprint"
                                            sub={prevSprintObj ? `${prevSprintObj.name} (Completed)` : 'No completed sprint found'}
                                            value="previous"
                                            selected={customScope}
                                            onChange={setCustomScope}
                                        />
                                        <ScopeCard
                                            label="Custom Date Range"
                                            sub={null}
                                            value="custom"
                                            selected={customScope}
                                            onChange={setCustomScope}
                                        />
                                        {customScope === 'custom' && (
                                            <div className="grid grid-cols-2 gap-2 mt-1">
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-[#424750] uppercase tracking-wider px-1">From</label>
                                                    <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)}
                                                        className="w-full bg-white border border-[#c2c6d2]/30 rounded-lg text-xs h-9 px-2 outline-none text-[#191c1d]" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[9px] font-bold text-[#424750] uppercase tracking-wider px-1">To</label>
                                                    <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)}
                                                        className="w-full bg-white border border-[#c2c6d2]/30 rounded-lg text-xs h-9 px-2 outline-none text-[#191c1d]" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* â”€â”€ Entities â”€â”€ */}
                                <div className="space-y-2">
                                    <SideLabel>Entities</SideLabel>
                                    <div className="grid grid-cols-2 gap-2">
                                        <SideSelect value={customDept} onChange={e => setCustomDept(e.target.value)}>
                                            <option value="all">All Departments</option>
                                            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                                        </SideSelect>
                                        <SideSelect value={customTeam} onChange={e => setCustomTeam(e.target.value)}>
                                            <option value="all">All Teams</option>
                                            {employees.map(e => <option key={e._id} value={e.id}>{e.name}{e.surname ? ` ${e.surname}` : ''}</option>)}
                                        </SideSelect>
                                    </div>
                                </div>

                                {/* â”€â”€ Project Filtering â”€â”€ */}
                                <div className="space-y-2">
                                    <SideLabel>Project Filtering</SideLabel>
                                    <div className="flex gap-2">
                                        {['CLIENT', 'IN-HOUSE'].map(type => (
                                            <button
                                                key={type}
                                                onClick={() => { setCustomProjectType(type); setCustomProject('all'); }}
                                                className={`flex-1 py-2 text-[10px] font-black rounded-lg border transition-all ${customProjectType === type ? 'bg-[#003263] text-white border-[#003263]' : 'bg-white text-[#424750] border-[#c2c6d2]/30 hover:border-[#003263]/40'}`}
                                            >{type}</button>
                                        ))}
                                    </div>
                                    <SideSelect value={customProject} onChange={e => setCustomProject(e.target.value)}>
                                        <option value="all">Select Project...</option>
                                        {filteredSidebarProjects.map(p => <option key={p._id} value={p.name}>{p.name}</option>)}
                                    </SideSelect>
                                </div>

                                {/* â”€â”€ Report Category â”€â”€ */}
                                <div className="space-y-2">
                                    <SideLabel>Report Category</SideLabel>
                                    <SideSelect value={customReportCategory} onChange={e => setCustomReportCategory(e.target.value)} fontBold>
                                        {REPORT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </SideSelect>
                                </div>

                                {/* â”€â”€ Include Content Sections â”€â”€ */}
                                <div className="space-y-3">
                                    <SideLabel>Include Content Sections</SideLabel>
                                    <div className="bg-white border border-[#c2c6d2]/20 rounded-xl p-4 space-y-2.5 max-h-52 overflow-y-auto srm-scroll">
                                        {INCLUDE_SECTIONS.map(({ key, label, icon }) => (
                                            <label key={key} className="flex items-center gap-3 cursor-pointer group">
                                                <input
                                                    type="checkbox"
                                                    checked={includeSections[key]}
                                                    onChange={e => setIncludeSections(prev => ({ ...prev, [key]: e.target.checked }))}
                                                    className="rounded text-[#003263] focus:ring-[#003263] w-4 h-4"
                                                />
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-[11px] font-semibold text-[#191c1d] group-hover:text-[#003263] transition-colors">{label}</span>
                                                    {icon && <span className="material-symbols-outlined text-[14px] text-[#03488a]" style={{ fontVariationSettings: "'FILL' 1" }}>{icon}</span>}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* â”€â”€ Error â”€â”€ */}
                                {genError && (
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                                        <p className="text-xs text-red-600 font-semibold">{genError}</p>
                                        <button onClick={() => setGenError(null)} className="text-[10px] text-red-400 hover:underline mt-1">Dismiss</button>
                                    </div>
                                )}
                            </div>

                            {/* â”€â”€ CTA â”€â”€ */}
                            <div className="px-6 py-5 border-t border-[#c2c6d2]/30 flex-shrink-0">
                                <button
                                    onClick={handleGenerateCustom}
                                    disabled={generating}
                                    className="w-full text-white py-4 rounded-xl font-headline font-extrabold text-xs shadow-lg hover:brightness-110 active:scale-[0.98] transition-all flex items-center justify-center gap-2 uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed"
                                    style={{ background: 'linear-gradient(90deg,#03488a 0%,#006591 100%)' }}
                                >
                                    {generating ? (
                                        <><Spin indicator={<LoadingOutlined style={{ fontSize: 14, color: '#fff' }} />} /><span>Generatingâ€¦</span></>
                                    ) : (
                                        <><span>Generate Custom Report</span><span className="material-symbols-outlined text-lg">rocket_launch</span></>
                                    )}
                                </button>
                                <p className="text-[10px] text-center text-[#424750] mt-3 opacity-70 font-medium">
                                    Heavy data sets may take up to 30 seconds to synthesize.
                                </p>
                            </div>
                        </aside>
                    </div>

                    {/* â”€â”€ FOOTER â”€â”€ */}
                    <footer className="h-12 bg-[#edeeef]/50 border-t border-[#c2c6d2]/20 px-8 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-[#28b5fd]" />
                                <span className="text-[10px] font-black text-[#424750] uppercase tracking-tighter">
                                    {reports.length} Report{reports.length !== 1 ? 's' : ''} Synthesized
                                </span>
                            </div>
                            {unreadCount > 0 && (
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-[#ba1a1a]" />
                                    <span className="text-[10px] font-black text-[#424750] uppercase tracking-tighter">
                                        {unreadCount} Critical Unread
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="text-[10px] text-[#424750] font-black uppercase tracking-widest opacity-50">
                            DH Intelligence Engine v4.2
                        </div>
                    </footer>
                </div>
            </div>
        </>
    );
}

// â”€â”€â”€ Sidebar helper sub-components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function SideLabel({ children }) {
    return <h3 className="text-[10px] font-black text-[#424750] uppercase tracking-widest px-1">{children}</h3>;
}

function SideSelect({ children, fontBold, ...props }) {
    return (
        <select
            {...props}
            className={`w-full bg-white border border-[#c2c6d2]/20 rounded-lg text-xs h-9 px-2 text-[#191c1d] outline-none ${fontBold ? 'font-bold h-10' : ''}`}
        >{children}</select>
    );
}

function ScopeCard({ label, sub, value, selected, onChange }) {
    const isSelected = selected === value;
    return (
        <label className={`flex items-center justify-between p-3 bg-white border rounded-xl cursor-pointer transition-colors ${isSelected ? 'border-[#003263]/40 shadow-sm' : 'border-[#c2c6d2]/20 hover:border-[#003263]/30'}`}>
            <div className="flex flex-col">
                <span className="text-xs font-bold text-[#191c1d]">{label}</span>
                {sub && <span className="text-[10px] text-[#424750] mt-0.5">{sub}</span>}
            </div>
            <input
                type="radio"
                name="customScope"
                checked={isSelected}
                onChange={() => onChange(value)}
                className="text-[#003263] focus:ring-[#003263] h-4 w-4 flex-shrink-0"
            />
        </label>
    );
}

function FilterSelect({ label, value, onChange, children }) {
    return (
        <div className="space-y-1">
            <label className="text-[10px] font-bold text-[#424750] uppercase tracking-wider px-1">{label}</label>
            <select value={value} onChange={e => onChange(e.target.value)} className="w-full bg-[#f3f4f5] border-none rounded-lg text-xs h-9 px-2 text-[#191c1d] outline-none">
                {children}
            </select>
        </div>
    );
}

function ActionBtn({ icon, title, cls, onClick }) {
    return (
        <button onClick={onClick} title={title} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${cls}`}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>{icon}</span>
        </button>
    );
}

